import { supabase } from '@/lib/supabase';
import { formatTimeToHHMM } from './seller-helpers';

/**
 * 가게 정보 조회 (사업자 정보 포함)
 */
export async function getStoreInfo(restaurantId: string): Promise<{
  success: boolean;
  data?: {
    restaurant: any;
    business: any;
  };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select(`
        *,
        businesses!inner(
          business_name,
          representative_name,
          business_number
        )
      `)
      .eq('id', restaurantId)
      .single();

    if (error) {
      console.error('Store info fetch error:', error);
      throw error;
    }

    return {
      success: true,
      data: {
        restaurant: data,
        business: data.businesses
      }
    };
  } catch (error) {
    console.error('Get store info error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '가게 정보를 불러오는데 실패했습니다'
    };
  }
}

// 오늘 영업시간 조회 (restaurant_operating_hours 기반)
export async function getTodayOperatingHours(restaurantId: string): Promise<{
  day_of_week: number;
  is_closed: boolean;
  open_time?: string | null;
  close_time?: string | null;
} | null> {
  try {
    // 실제로는 JS Date에서 getDay()가 로컬을 쓰므로, 서버 타임존이 UTC라면 단순 getDay()로도 KST와 어긋날 수 있음.
    // 안전하게 KST 기준 요일을 계산
    const kstOffsetMs = 9 * 60 * 60 * 1000;
    const kstNow = new Date(Date.now() + kstOffsetMs);
    const dayOfWeek = kstNow.getUTCDay(); // 0=Sun

    const { data, error } = await supabase
      .from('restaurant_operating_hours')
      .select('day_of_week, is_closed, open_time, close_time')
      .eq('restaurant_id', restaurantId)
      .eq('day_of_week', dayOfWeek)
      .single();

    if (error) {
      console.error('Get operating hours error:', error);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Get operating hours error:', err);
    return null;
  }
}

/**
 * 가게 정보 수정
 */
export async function updateStoreInfo(restaurantId: string, data: {
  name?: string;
  image_url?: string;
  bestmenu1?: string;
  bestmenu2?: string;
  bestmenu3?: string;
  address?: string;
  phone?: string;
  notice?: string;
  description?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    // 제공된 필드만 업데이트
    if (data.name !== undefined) updateData.name = data.name;
    if (data.image_url !== undefined) updateData.image_url = data.image_url;
    if (data.bestmenu1 !== undefined) updateData.bestmenu1 = data.bestmenu1;
    if (data.bestmenu2 !== undefined) updateData.bestmenu2 = data.bestmenu2;
    if (data.bestmenu3 !== undefined) updateData.bestmenu3 = data.bestmenu3;
    if (data.address !== undefined) updateData.address = data.address;
    if (data.phone !== undefined) updateData.phone = data.phone;
    if (data.notice !== undefined) updateData.notice = data.notice;
    if (data.description !== undefined) updateData.description = data.description;

    const { error } = await supabase
      .from('restaurants')
      .update(updateData)
      .eq('id', restaurantId);

    if (error) {
      console.error('Store info update error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Update store info error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '가게 정보 수정에 실패했습니다'
    };
  }
}

/**
 * 마감할인 토글 상태 조회
 */
export async function getDeadlineDiscountToggle(restaurantId: string): Promise<{ success: boolean; enabled?: boolean; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('deadline_discount_enabled')
      .eq('id', restaurantId)
      .single();

    if (error) throw error;

    return { success: true, enabled: data?.deadline_discount_enabled ?? true };
  } catch (error) {
    console.error('Get deadline discount toggle error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '마감할인 토글 조회에 실패했습니다'
    };
  }
}

/**
 * 마감할인 토글 업데이트
 */
export async function updateDeadlineDiscountToggle(restaurantId: string, enabled: boolean): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({
        deadline_discount_enabled: enabled,
        updated_at: new Date().toISOString()
      })
      .eq('id', restaurantId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Update deadline discount toggle error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '마감할인 토글 변경에 실패했습니다'
    };
  }
}

/**
 * 영업시간 정보 조회 - 7개 요일 전체를 DayOperatingHours[] 배열로 반환
 */
export async function getOperatingHours(restaurantId: string): Promise<{
  success: boolean;
  data?: any[];
  error?: string;
}> {
  try {
    // 7개 요일 전체 조회
    const { data: dbRecords, error } = await supabase
      .from('restaurant_operating_hours')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('day_of_week', { ascending: true });

    if (error) {
      console.error('Operating hours fetch error:', error);
      throw error;
    }

    // DayOperatingHours[] 형식으로 변환
    const operatingHours = convertDbToComponentFormat(dbRecords || []);

    return {
      success: true,
      data: operatingHours
    };
  } catch (error) {
    console.error('Get operating hours error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '영업시간 정보를 불러오는데 실패했습니다'
    };
  }
}

/**
 * DB 레코드를 컴포넌트 형식으로 변환 (DayOperatingHours[])
 */
function convertDbToComponentFormat(dbRecords: any[]): any[] {
  const operatingHours = [];
  
  // 0=일요일, 1=월요일, ..., 6=토요일 순서로 7개 요일 생성
  for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
    const dbRecord = dbRecords.find(record => record.day_of_week === dayOfWeek);
    
    if (dbRecord) {
      // DB 레코드가 있는 경우
      operatingHours.push({
        dayOfWeek: dayOfWeek,
        isClosed: dbRecord.is_closed || false,
        openTime: formatTimeToHHMM(dbRecord.open_time) || '',
        closeTime: formatTimeToHHMM(dbRecord.close_time) || '',
        breakStartTime: formatTimeToHHMM(dbRecord.break_start_time) || '',
        breakEndTime: formatTimeToHHMM(dbRecord.break_end_time) || '',
        hasBreakTime: !!(dbRecord.break_start_time && dbRecord.break_end_time)
      });
    } else {
      // DB 레코드가 없는 경우 기본값
      operatingHours.push({
        dayOfWeek: dayOfWeek,
        isClosed: false,
        openTime: '',
        closeTime: '',
        breakStartTime: '',
        breakEndTime: '',
        hasBreakTime: false
      });
    }
  }
  
  return operatingHours;
}

/**
 * 영업시간 정보 업데이트 - DayOperatingHours[] 배열을 받아서 7개 레코드 처리
 */
export async function updateOperatingHours(restaurantId: string, operatingHoursData: any[]): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // 기존 영업시간 정보 삭제 (upsert 방식으로 전체 교체)
    await supabase
      .from('restaurant_operating_hours')
      .delete()
      .eq('restaurant_id', restaurantId);

    // 새로운 영업시간 정보 삽입
    const dbRecords = convertComponentToDbFormat(restaurantId, operatingHoursData);
    
    if (dbRecords.length > 0) {
      const { error } = await supabase
        .from('restaurant_operating_hours')
        .insert(dbRecords);

      if (error) throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Update operating hours error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '영업시간 정보 수정에 실패했습니다'
    };
  }
}

/**
 * 컴포넌트 형식을 DB 레코드 형식으로 변환
 */
function convertComponentToDbFormat(restaurantId: string, operatingHoursData: any[]): any[] {
  return operatingHoursData.map((dayData) => ({
    restaurant_id: restaurantId,
    day_of_week: dayData.dayOfWeek,
    is_closed: dayData.isClosed || false,
    open_time: dayData.isClosed || !dayData.openTime?.trim() ? null : dayData.openTime,
    close_time: dayData.isClosed || !dayData.closeTime?.trim() ? null : dayData.closeTime,
    break_start_time: dayData.isClosed || !dayData.hasBreakTime || !dayData.breakStartTime?.trim() ? null : dayData.breakStartTime,
    break_end_time: dayData.isClosed || !dayData.hasBreakTime || !dayData.breakEndTime?.trim() ? null : dayData.breakEndTime,
  }));
}

/**
 * 가게 영업 상태 조회
 */
export async function getRestaurantStatus(restaurantId: string): Promise<{
  success: boolean;
  data?: { is_open: boolean };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('is_open')
      .eq('id', restaurantId)
      .single();

    if (error) {
      console.error('Restaurant status fetch error:', error);
      throw error;
    }

    return {
      success: true,
      data: { is_open: data.is_open }
    };
  } catch (error) {
    console.error('Get restaurant status error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '영업 상태를 불러오는데 실패했습니다'
    };
  }
}

/**
 * 마감할인 주문시간 커스텀 설정 조회
 */
export async function getDeadlineOrderWindow(restaurantId: string): Promise<{
  success: boolean;
  data?: {
    useCustom: boolean;
    customOrderStartTime: string | null;
    customOrderEndTime: string | null;
    deadlineOrderStartTime: string | null;
    deadlineOrderEndTime: string | null;
  };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('use_custom_deadline_order_window, custom_deadline_order_start_time, custom_deadline_order_end_time, deadline_discount_start_time, deadline_discount_end_time')
      .eq('id', restaurantId)
      .single();

    if (error) {
      console.error('Get deadline order window error:', error);
      throw error;
    }

    return {
      success: true,
      data: {
        useCustom: data?.use_custom_deadline_order_window ?? false,
        customOrderStartTime: data?.custom_deadline_order_start_time ?? null,
        customOrderEndTime: data?.custom_deadline_order_end_time ?? null,
        deadlineOrderStartTime: data?.deadline_discount_start_time ?? null,
        deadlineOrderEndTime: data?.deadline_discount_end_time ?? null
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문시간 설정을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 가게 영업 상태 토글
 */
export async function toggleRestaurantStatus(restaurantId: string, isOpen: boolean): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({ 
        is_open: isOpen,
        updated_at: new Date().toISOString()
      })
      .eq('id', restaurantId);

    if (error) {
      console.error('Restaurant status toggle error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Toggle restaurant status error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '영업 상태 변경에 실패했습니다'
    };
  }
}

/**
 * 예약할인 활성화 상태 조회
 */
export async function getReservationDiscountStatus(restaurantId: string): Promise<{
  success: boolean;
  data?: { reservation_discount_enabled: boolean };
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .select('reservation_discount_enabled')
      .eq('id', restaurantId)
      .single();

    if (error) {
      console.error('Reservation discount status fetch error:', error);
      throw error;
    }

    return {
      success: true,
      data: { reservation_discount_enabled: data.reservation_discount_enabled || false }
    };
  } catch (error) {
    console.error('Get reservation discount status error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '예약할인 상태를 불러오는데 실패했습니다'
    };
  }
}

/**
 * 예약할인 활성화 상태 토글
 */
export async function toggleReservationDiscount(restaurantId: string, enabled: boolean): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('restaurants')
      .update({ 
        reservation_discount_enabled: enabled,
        updated_at: new Date().toISOString()
      })
      .eq('id', restaurantId);

    if (error) {
      console.error('Reservation discount toggle error:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Toggle reservation discount error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '예약할인 상태 변경에 실패했습니다'
    };
  }
}
